import {
  AddressBookIcon,
  BugIcon,
  CubeIcon,
  DatabaseIcon,
  DesktopIcon,
  EnvelopeSimpleIcon,
  FilePdfIcon,
  FunnelIcon,
  GearIcon,
  Icon,
  KeyIcon,
  ListBulletsIcon,
  LockKeyIcon,
  NoteIcon,
  PaletteIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  RobotIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  SquaresFourIcon,
  TableIcon,
  TimerIcon,
  UsersIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { FeatureFlagKey } from '@erp/shared-types';

export type OrganizationSettingsSectionId =
  | 'address-book'
  | 'agent-resources'
  | 'agents'
  | 'api-keys'
  | 'approvals'
  | 'avd-login-test'
  | 'azure-email-registration'
  | 'brand-kit'
  | 'control-plane'
  | 'credentials'
  | 'custom-fields'
  | 'data-mappings'
  | 'default-recipients'
  | 'edi'
  | 'email-templates'
  | 'enums'
  | 'famous'
  | 'general'
  | 'guardrail-policies'
  | 'internal-ui-templates'
  | 'known-domains'
  | 'data-sources'
  | 'members'
  | 'memory-bank-notes'
  | 'modules'
  | 'otp-secrets'
  | 'pdf-templates'
  | 'procurement'
  | 'resource-privileges'
  | 'sales-order-review-ui'
  | 'sandbox-config'
  | 'schema-definitions'
  | 'sentry-test'
  | 'teams'
  | 'template-blocks'
  | 'test-forward-original-graph-message'
  | 'test-timeout'
  | 'test-upn-write-access'
  | 'voice-agents';

export type OrganizationSettingsGroupId =
  | 'general'
  | 'team'
  | 'data-model'
  | 'design'
  | 'communication'
  | 'security'
  | 'ai'
  | 'integrations'
  | 'developer';

export type OrganizationSettingsSection = {
  id: OrganizationSettingsSectionId;
  title: string;
  subtitle?: string;
  icon: Icon;
  group: OrganizationSettingsGroupId;
  gentlyOnly?: boolean;
  requiredModule?: ProductModule;
  /**
   * Hide this section unless the viewer holds the listed permission. Gently
   * staff bypass this via the `gently` flag in the filter — only set this for
   * sections whose page would otherwise 403 for users without the permission.
   */
  requiredPermission?: { action: Action; subject: Subject };
  featureFlag?: FeatureFlagKey;
};

export type OrganizationSettingsGroup = {
  id: OrganizationSettingsGroupId;
  title: string;
};

export const ORGANIZATION_SETTINGS_GROUPS: OrganizationSettingsGroup[] = [
  { id: 'general', title: 'General' },
  { id: 'team', title: 'Team & Access' },
  { id: 'data-model', title: 'Data Model' },
  { id: 'design', title: 'Design & Pages' },
  { id: 'communication', title: 'Communication' },
  { id: 'security', title: 'Security' },
  { id: 'ai', title: 'AI' },
  { id: 'integrations', title: 'Integrations' },
  { id: 'developer', title: 'Developer Tools' },
];

export const ORGANIZATION_SETTINGS_SECTIONS: OrganizationSettingsSection[] = [
  {
    id: 'general',
    title: 'Organization',
    subtitle: 'Core organization settings',
    icon: GearIcon,
    group: 'general',
    gentlyOnly: true,
  },
  {
    id: 'sales-order-review-ui',
    title: 'Sales Order Review page',
    subtitle: 'Metrics, margin, columns, fields & layout',
    icon: PaletteIcon,
    group: 'design',
    gentlyOnly: true,
  },
  {
    id: 'members',
    title: 'Members',
    subtitle: 'Manage members of your organization',
    icon: UsersIcon,
    group: 'team',
    requiredPermission: { action: Action.Read, subject: Subject.User },
  },
  {
    id: 'teams',
    title: 'Teams',
    subtitle: 'Group members into teams',
    icon: UsersThreeIcon,
    group: 'team',
    requiredPermission: { action: Action.Read, subject: Subject.UserGroup },
  },
  {
    id: 'agents',
    title: 'Agents',
    subtitle:
      'AI agents on your team, the tools they use, and what they remember',
    icon: RobotIcon,
    group: 'team',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.BackgroundAgent,
    },
  },
  {
    id: 'api-keys',
    title: 'API Keys',
    subtitle: 'Manage API keys for Data Sync Agents',
    icon: KeyIcon,
    group: 'team',
    requiredPermission: { action: Action.Read, subject: Subject.ApiKey },
  },
  {
    id: 'custom-fields',
    title: 'Custom Fields',
    subtitle: 'Manage your core custom fields',
    icon: FunnelIcon,
    group: 'data-model',
  },
  {
    id: 'enums',
    title: 'Enums',
    subtitle: 'Manage your core enums',
    icon: CubeIcon,
    group: 'data-model',
  },
  {
    id: 'schema-definitions',
    title: 'Schema Definitions',
    subtitle: 'Configure extraction and extension schemas',
    icon: ListBulletsIcon,
    group: 'data-model',
  },
  {
    id: 'data-mappings',
    title: 'Data Mappings',
    subtitle: 'Manage customer data mappings for business logic',
    icon: TableIcon,
    group: 'data-model',
  },
  {
    id: 'approvals',
    title: 'Approvals',
    subtitle: 'Configure discount and order approval levels and rules',
    icon: ShieldCheckIcon,
    group: 'data-model',
    // Gated to Gently staff until the dedicated Subject.ApprovalConfig RBAC
    // wiring lands on the backend. Approval thresholds are sensitive
    // configuration — better to hide the surface than expose it ungated.
    gentlyOnly: true,
  },
  {
    id: 'data-sources',
    title: 'Data Sources',
    subtitle: 'Configure and monitor AS/400 and vendor price list data sources',
    icon: DatabaseIcon,
    group: 'data-model',
    gentlyOnly: true,
    featureFlag: 'data-sources-enabled',
  },
  {
    id: 'address-book',
    title: 'Address Book',
    subtitle: 'Manage your organization’s contacts and saved email addresses',
    icon: AddressBookIcon,
    group: 'communication',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.Contact,
    },
  },
  {
    id: 'default-recipients',
    title: 'Default Recipients',
    subtitle: "Recipients automatically CC'd on composed replies",
    icon: PaperPlaneTiltIcon,
    group: 'communication',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.OrganizationConfig,
    },
  },
  {
    id: 'email-templates',
    title: 'Email Templates',
    subtitle: 'Manage reusable email templates with dynamic variables',
    icon: EnvelopeSimpleIcon,
    group: 'communication',
  },
  {
    id: 'pdf-templates',
    title: 'PDF Templates',
    subtitle: 'Manage reusable PDF document templates with dynamic variables',
    icon: FilePdfIcon,
    group: 'communication',
  },
  {
    id: 'template-blocks',
    title: 'Template Blocks',
    subtitle: 'Manage reusable JSON-render fragments for email templates',
    icon: NoteIcon,
    group: 'communication',
  },
  {
    id: 'internal-ui-templates',
    title: 'Custom UI',
    subtitle:
      'AI-generated and authored screens rendered live with your design system',
    icon: SquaresFourIcon,
    group: 'communication',
  },
  {
    id: 'brand-kit',
    title: 'Brand Kit',
    subtitle: 'Your brand colors, typography, logos, and voice for emails',
    icon: PaletteIcon,
    group: 'communication',
  },
  {
    id: 'azure-email-registration',
    title: 'Azure Email Registration',
    subtitle: 'Configure email registration from your Azure tenant',
    icon: EnvelopeSimpleIcon,
    group: 'communication',
    gentlyOnly: true,
  },
  {
    id: 'known-domains',
    title: 'Known Domains',
    subtitle: 'Configure internal/SSO domains for user authentication',
    icon: GearIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'resource-privileges',
    title: 'Resource Privileges',
    subtitle:
      'Tenant-level data scoping — control which records each principal can see',
    icon: ShieldCheckIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'sandbox-config',
    title: 'Code Sandbox',
    subtitle:
      'Configure resource limits, network access, and environment for code sandboxes',
    icon: ShieldCheckIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'control-plane',
    title: 'Control Plane',
    subtitle: 'Connection status and management',
    icon: CubeIcon,
    group: 'integrations',
    gentlyOnly: true,
  },
  {
    id: 'credentials',
    title: 'Credentials',
    subtitle: 'Manage passwords, tokens, and other secrets',
    icon: LockKeyIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'otp-secrets',
    title: 'OTP Secrets',
    subtitle: 'Manage TOTP secrets for automated MFA',
    icon: LockKeyIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'agent-resources',
    title: 'Agent Resources',
    subtitle:
      'Manage concurrency-gated resources that agents can exclusively hold',
    icon: DatabaseIcon,
    group: 'ai',
    gentlyOnly: true,
  },
  {
    id: 'guardrail-policies',
    title: 'Guardrail Policies',
    subtitle: 'Create and manage AI safety policies for workflow steps',
    icon: ShieldCheckIcon,
    group: 'ai',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.GuardrailPolicy,
    },
  },
  {
    id: 'memory-bank-notes',
    title: 'Memory Bank Notes',
    subtitle: 'Manage and search through your AI memory bank notes',
    icon: NoteIcon,
    group: 'ai',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.MemoryBankNote,
    },
  },
  {
    id: 'modules',
    title: 'Modules',
    subtitle: 'Setup and manage your modules',
    icon: KeyIcon,
    group: 'integrations',
    gentlyOnly: true,
  },
  {
    id: 'edi',
    title: 'EDI',
    subtitle: 'EDI settings',
    icon: ShoppingBagIcon,
    group: 'integrations',
    requiredModule: ProductModule.ORDERFUL_EDI,
  },
  {
    id: 'famous',
    title: 'FAMOUS™',
    subtitle: 'FAMOUS™ integration settings',
    icon: ShoppingBagIcon,
    group: 'integrations',
    requiredModule: ProductModule.FAMOUS_INTEGRATION,
  },
  {
    id: 'voice-agents',
    title: 'Voice Agents',
    subtitle: 'Configure AI voice agents for outbound calls',
    icon: PhoneIcon,
    group: 'ai',
    gentlyOnly: true,
  },
  {
    id: 'procurement',
    title: 'Procurement',
    subtitle: 'Voice agent & number to call for procurement voice flows',
    icon: PhoneIcon,
    group: 'integrations',
    requiredModule: ProductModule.PROCUREMENT,
  },
  {
    id: 'avd-login-test',
    title: 'AVD Login Test',
    subtitle: 'Test Azure Virtual Desktop login automation',
    icon: DesktopIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'sentry-test',
    title: 'Trigger Sentry Error',
    subtitle: 'Intentionally throw an error to test Sentry',
    icon: BugIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'test-timeout',
    title: 'Test Timeout',
    subtitle: 'Call backend to delay response by specified milliseconds',
    icon: TimerIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'test-upn-write-access',
    title: 'Test UPN Write Access',
    subtitle: 'Send a test email from a UPN to verify write permissions',
    icon: EnvelopeSimpleIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'test-forward-original-graph-message',
    title: 'Test Forward Original Message',
    subtitle:
      'Forward an existing Graph message (order-notification email path)',
    icon: PaperPlaneTiltIcon,
    group: 'developer',
    gentlyOnly: true,
  },
];

export const DEFAULT_ORG_SECTION_ID: OrganizationSettingsSectionId =
  'custom-fields';

export const SECTION_PATH_PREFIX = '/settings/organization';

export const sectionPath = (id: OrganizationSettingsSectionId) =>
  `${SECTION_PATH_PREFIX}/${id}` as const;

// Legacy slug → current slug map. Used by the legacy $section redirect route so
// bookmarked URLs keep working.
export const LEGACY_SECTION_SLUG_MAP: Record<
  string,
  OrganizationSettingsSectionId
> = {
  'organization-settings': 'general',
  'core-custom-fields': 'custom-fields',
  'core-enums': 'enums',
  'tag-definitions': 'resource-privileges',
  'tag-resources': 'resource-privileges',
  tags: 'resource-privileges',
};

export const SECTION_LABELS = ORGANIZATION_SETTINGS_SECTIONS.reduce<
  Record<string, string>
>((acc, section) => {
  acc[section.id] = section.title;
  return acc;
}, {});

export const getOrganizationSettingsSections = ({
  gently,
  isModuleEnabled,
  hasPermission,
  isFeatureFlagEnabled,
}: {
  gently: boolean;
  isModuleEnabled: (module: ProductModule) => boolean;
  hasPermission: (action: Action, subject: Subject) => boolean;
  isFeatureFlagEnabled: (flag: FeatureFlagKey) => boolean;
}) =>
  ORGANIZATION_SETTINGS_SECTIONS.filter((section) => {
    if (section.gentlyOnly && !gently) {
      return false;
    }
    if (section.requiredModule && !isModuleEnabled(section.requiredModule)) {
      return false;
    }
    if (
      section.requiredPermission &&
      !gently &&
      !hasPermission(
        section.requiredPermission.action,
        section.requiredPermission.subject,
      )
    ) {
      return false;
    }
    if (section.featureFlag && !isFeatureFlagEnabled(section.featureFlag)) {
      return false;
    }
    return true;
  });
